import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import portalApi from '../lib/portalApi';
import { toast } from 'sonner';
import { Lock, Eye, EyeOff, Shield, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';

export const ClientPortalLogin = () => {
  const { accessToken } = useParams();
  const navigate = useNavigate();
  
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!password) {
      toast.error('Por favor ingresa tu contraseña');
      return;
    }

    try {
      setLoading(true);
      
      console.log('Attempting login with token:', accessToken);
      
      const response = await portalApi.post('/client-portal/auth/login', {
        access_token: accessToken,
        password: password
      });

      console.log('Login response:', response.data);

      // Guardar session token y datos en localStorage
      localStorage.setItem('portal_session', response.data.session_token);
      localStorage.setItem('portal_contractor_name', response.data.contractor_name);
      localStorage.setItem('portal_expires_at', response.data.expires_at);
      localStorage.setItem('portal_access_id', response.data.access_id);

      console.log('Saved to localStorage, navigating to dashboard...');

      toast.success(`¡Bienvenido ${response.data.contractor_name}!`);
      
      // Dar tiempo para que se guarde en localStorage
      setTimeout(() => {
        navigate('/portal/dashboard');
      }, 100);
      
    } catch (error) {
      console.error('Error logging in:', error);
      
      if (error.response?.status === 429) {
        toast.error('Demasiados intentos. Espera 15 minutos.');
      } else if (error.response?.status === 403) {
        toast.error(error.response.data.detail || 'Acceso denegado');
      } else {
        toast.error('Contraseña incorrecta. Verifica tus credenciales.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo y Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-full mb-4">
            <Shield className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2">
            Portal de Clientes
          </h1>
          <p className="text-slate-600">
            Asistelo - Sistema de Gestión Avanzada
          </p>
        </div>

        {/* Card de Login */}
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle className="text-xl">Acceso al Portal</CardTitle>
            <CardDescription>
              Ingresa tu contraseña para ver tus pólizas
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Contraseña */}
              <div>
                <Label htmlFor="password">Contraseña de Acceso</Label>
                <div className="relative mt-2">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <Lock className="h-5 w-5 text-slate-400" />
                  </div>
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Ingresa tu contraseña"
                    className="pl-10 pr-10"
                    autoComplete="off"
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 flex items-center pr-3"
                    disabled={loading}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5 text-slate-400" />
                    ) : (
                      <Eye className="h-5 w-5 text-slate-400" />
                    )}
                  </button>
                </div>
              </div>

              {/* Botón de acceso */}
              <Button 
                type="submit" 
                className="w-full" 
                disabled={loading}
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Ingresando...
                  </>
                ) : (
                  <>
                    <Lock className="h-4 w-4 mr-2" />
                    Ingresar al Portal
                  </>
                )}
              </Button>
            </form>

            {/* Info de seguridad */}
            <Alert className="mt-6 bg-blue-50 border-blue-200">
              <AlertCircle className="h-4 w-4 text-blue-600" />
              <AlertDescription className="text-blue-800 text-sm">
                <strong>Acceso seguro.</strong> Si no tienes tu contraseña o tienes problemas para acceder, 
                contacta a tu asesor de seguros.
              </AlertDescription>
            </Alert>

            {/* Ayuda adicional */}
            <div className="mt-6 text-center">
              <p className="text-xs text-slate-500">
                Este portal es solo para clientes autorizados.
                <br />
                La contraseña fue proporcionada por tu asesor.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center mt-6 text-sm text-slate-500">
          <p>© 2026 Asístelo. Todos los derechos reservados.</p>
        </div>
      </div>
    </div>
  );
};

export default ClientPortalLogin;
